import axios from 'axios';
import { apiClient, apiClientWithToken } from './client';
import { resolveApiErrorMessage } from './errorResponse';

export interface SignUpConsentParams {
  adminConsent: string;
  tenant: string;
  state: string;
  error?: string;
  errorDescription?: string;
}

export interface ValidationError {
  status: number;
  reason: string;
  message: string;
}

export interface TenantInitResponse {
  organizationId: string;
  jobId: string;
}

/**
 * Server-side consent validation. Mirrors the V2 backend's
 * `GET /api/v2/signUp?admin_consent=...&tenant=...&state=...`.
 * Returns nothing on success (HTTP 204); throws `ValidationError` on 4xx.
 */
export const validateSignUpConsent = async (params: SignUpConsentParams): Promise<void> => {
  const query: Record<string, string> = {
    admin_consent: params.adminConsent,
    tenant: params.tenant,
    state: params.state,
  };
  if (params.error) query.error = params.error;
  if (params.errorDescription) query.error_description = params.errorDescription;

  try {
    await apiClient.get('/api/v2/signUp', { params: query });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw {
        status: error.response?.status ?? 0,
        reason: error.response?.statusText ?? 'Unknown error',
        message: resolveApiErrorMessage(error, error.message),
      } satisfies ValidationError;
    }
    throw error;
  }
};

/**
 * Triggers tenant initialization on the backend
 * (`POST /api/v2/signUp/{invitationId}/{tenantId}`). Returns the
 * organization id (used to build the polling URL) and the job id.
 */
export const triggerSignUp = async (
  token: string,
  invitationId: string,
  tenantId: string,
): Promise<TenantInitResponse> => {
  const response = await apiClientWithToken(token).post<TenantInitResponse>(
    `/api/v2/signUp/${invitationId}/${tenantId}`,
  );
  return response.data;
};
